import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState, useCallback } from 'react'
import { useAuth } from '@/contexts/auth-context'
import { AutoBreadcrumb } from '@/components/ui/auto-breadcrumb'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { DeleteConfirmationDialog } from '@/components/ui/delete-confirmation-dialog'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Loader2, Trash2, Maximize2, Download } from 'lucide-react'
import { toast } from 'sonner'
import {
  listAdminFeedbacks,
  updateFeedbackStatus,
  updateAdminFeedback,
  deleteFeedback,
  getFeedbackAttachmentDownloadUrl,
  FeedbackPublic,
} from '@/services/feedback-service'

export const Route = createFileRoute('/_authenticated/admin/feedbacks')({
  component: AdminFeedbacksPage,
})

function AdminFeedbacksPage() {
  const { user } = useAuth()
  const [feedbacks, setFeedbacks] = useState<FeedbackPublic[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [feedbackToDelete, setFeedbackToDelete] = useState<FeedbackPublic | null>(null)
  const [feedbackToView, setFeedbackToView] = useState<FeedbackPublic | null>(null)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)

  const handleDownloadAttachment = async (feedbackId: string, attachmentId: string) => {
    const key = `${feedbackId}-${attachmentId}`
    try {
      setDownloadingId(key)
      const { download_url, filename } = await getFeedbackAttachmentDownloadUrl(feedbackId, attachmentId)
      const a = document.createElement('a')
      a.href = download_url
      a.download = filename || 'attachment'
      a.rel = 'noopener noreferrer'
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (err) {
      toast.error('Failed to get download link', { description: (err as Error).message })
    } finally {
      setDownloadingId(null)
    }
  }

  const loadFeedbacks = useCallback(async () => {
    try {
      setLoading(true)
      const data = await listAdminFeedbacks()
      setFeedbacks(data)
      setError(null)
    } catch (err) {
      console.error(err)
      setError('Failed to load feedbacks')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (user?.is_admin) {
      loadFeedbacks()
    } else {
      setLoading(false)
    }
  }, [user?.is_admin, loadFeedbacks])

  const handleStatusChange = async (feedbackId: string, newStatus: 'new' | 'reviewed' | 'resolved') => {
    try {
      await updateFeedbackStatus(feedbackId, newStatus)
      toast.success('Status updated')
      setFeedbacks((prev) =>
        prev.map((fb) =>
          fb.feedback_id === feedbackId ? { ...fb, status: newStatus } : fb
        )
      )
      setFeedbackToView((prev) => (prev?.feedback_id === feedbackId ? { ...prev, status: newStatus } : prev))
    } catch (err) {
      toast.error('Failed to update status', { description: (err as Error).message })
    }
  }

  const handlePriorityChange = async (feedbackId: string, newPriority: 'low' | 'medium' | 'high' | 'critical') => {
    try {
      await updateAdminFeedback(feedbackId, { priority: newPriority })
      toast.success('Priority updated')
      setFeedbacks((prev) =>
        prev.map((fb) =>
          fb.feedback_id === feedbackId ? { ...fb, priority: newPriority } : fb
        )
      )
      setFeedbackToView((prev) => (prev?.feedback_id === feedbackId ? { ...prev, priority: newPriority } : prev))
    } catch (err) {
      toast.error('Failed to update priority', { description: (err as Error).message })
    }
  }

  const handleDelete = async () => {
    if (!feedbackToDelete) return
    try {
      await deleteFeedback(feedbackToDelete.feedback_id)
      toast.success('Feedback deleted')
      setFeedbacks((prev) => prev.filter((fb) => fb.feedback_id !== feedbackToDelete.feedback_id))
      if (feedbackToView?.feedback_id === feedbackToDelete.feedback_id) {
        setFeedbackToView(null)
      }
      setFeedbackToDelete(null)
    } catch {
      toast.error('Failed to delete feedback')
    }
  }

  if (!user?.is_admin) {
    return (
      <div className="flex flex-col h-screen">
        <main className="flex-1 overflow-auto p-4 md:p-6">
          <AutoBreadcrumb />
          <Card className="mt-6 p-6 max-w-2xl">
            <h1 className="text-xl font-semibold">Admin access required</h1>
            <p className="text-sm text-muted-foreground mt-2">
              You do not have permission to view this page.
            </p>
          </Card>
        </main>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-screen">
      <main className="flex-1 overflow-auto p-4 md:p-6">
        <AutoBreadcrumb />
        <div className="mt-6 space-y-6">
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-semibold">User Feedbacks</h1>
            <Button variant="outline" size="sm" onClick={loadFeedbacks} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Refresh'}
            </Button>
          </div>

          <Card className="p-0 overflow-hidden">
            {loading && feedbacks.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin mb-4" />
                <p>Loading feedbacks...</p>
              </div>
            ) : error ? (
              <div className="p-12 text-center">
                <p className="text-destructive mb-4">{error}</p>
                <Button onClick={loadFeedbacks}>Try Again</Button>
              </div>
            ) : feedbacks.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <p>No feedbacks received yet.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead className="w-1/3">Message</TableHead>
                    <TableHead>Attachments</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {feedbacks.map((feedback) => (
                    <TableRow key={feedback.feedback_id}>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium text-xs truncate max-w-[150px]" title={feedback.user_email}>
                            {feedback.user_email}
                          </span>
                          <span className="text-[10px] text-muted-foreground font-mono uppercase">
                            {feedback.user_id.slice(-8)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={
                          feedback.category === 'bug' ? 'destructive' :
                          feedback.category === 'feature' ? 'default' :
                          feedback.category === 'improvement' ? 'secondary' : 'outline'
                        } className="capitalize">
                          {feedback.category}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`capitalize ${
                            feedback.priority === 'critical'
                              ? 'bg-red-500/15 text-red-600 border-red-500/30'
                              : feedback.priority === 'high'
                                ? 'bg-orange-500/15 text-orange-600 border-orange-500/30'
                                : feedback.priority === 'medium'
                                  ? 'bg-amber-500/15 text-amber-600 border-amber-500/30'
                                  : 'bg-slate-500/10 text-slate-600 border-slate-500/20'
                          }`}
                        >
                          {feedback.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <button
                          type="button"
                          onClick={() => setFeedbackToView(feedback)}
                          title="View full message"
                          className="w-full text-left group flex items-start gap-2 hover:bg-muted/50 rounded-md p-2 -m-2 transition-colors"
                        >
                          <p className="text-sm whitespace-pre-wrap break-words line-clamp-3 flex-1 min-w-0">
                            {feedback.message}
                          </p>
                          <Maximize2 className="h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden />
                        </button>
                      </TableCell>
                      <TableCell>
                        {(feedback.attachments?.length ?? 0) > 0 ? (
                          <Badge variant="secondary" className="font-normal">
                            {feedback.attachments!.length} file{feedback.attachments!.length !== 1 ? 's' : ''}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(feedback.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={feedback.status}
                          onValueChange={(val: 'new' | 'reviewed' | 'resolved') => handleStatusChange(feedback.feedback_id, val)}
                        >
                          <SelectTrigger className="w-[120px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="reviewed">Reviewed</SelectItem>
                            <SelectItem value="resolved">Resolved</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => setFeedbackToDelete(feedback)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </div>
      </main>

      <DeleteConfirmationDialog
        open={!!feedbackToDelete}
        onOpenChange={(open) => !open && setFeedbackToDelete(null)}
        title="Delete Feedback"
        description="Are you sure you want to delete this feedback? This action cannot be undone."
        itemName={feedbackToDelete?.message || ''}
        onConfirm={handleDelete}
      />

      <Dialog open={!!feedbackToView} onOpenChange={(open) => !open && setFeedbackToView(null)}>
        <DialogContent size="xl" className="max-h-[85vh] flex flex-col px-8 py-8">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {feedbackToView && (
                <Badge
                  variant={
                    feedbackToView.category === 'bug'
                      ? 'destructive'
                      : feedbackToView.category === 'feature'
                        ? 'default'
                        : feedbackToView.category === 'improvement'
                          ? 'secondary'
                          : 'outline'
                  }
                  className="capitalize"
                >
                  {feedbackToView.category}
                </Badge>
              )}
              Feedback
            </DialogTitle>
          </DialogHeader>
          {feedbackToView && (
            <div className="flex flex-col gap-4 overflow-auto flex-1 min-h-0 pr-1">
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">From</p>
                <p className="text-sm font-medium">{feedbackToView.user_email}</p>
                <p className="text-[10px] text-muted-foreground font-mono uppercase">
                  {feedbackToView.user_id.slice(-8)}
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Status</p>
                <Select
                  value={feedbackToView.status}
                  onValueChange={(val: 'new' | 'reviewed' | 'resolved') =>
                    handleStatusChange(feedbackToView.feedback_id, val)
                  }
                >
                  <SelectTrigger className="w-[160px] h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="reviewed">Reviewed</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground">Priority</p>
                <Select
                  value={feedbackToView.priority}
                  onValueChange={(val: 'low' | 'medium' | 'high' | 'critical') =>
                    handlePriorityChange(feedbackToView.feedback_id, val)
                  }
                >
                  <SelectTrigger className="w-[160px] h-9 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <p className="text-xs font-medium text-muted-foreground">Date</p>
                <p className="text-sm">
                  {new Date(feedbackToView.created_at).toLocaleString()}
                </p>
              </div>
              <div className="space-y-1 flex-1 min-h-0 flex flex-col">
                <p className="text-xs font-medium text-muted-foreground">Message</p>
                <div className="flex-1 overflow-auto rounded-md border bg-muted/30 p-4">
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {feedbackToView.message}
                  </p>
                </div>
              </div>
              {feedbackToView.attachments && feedbackToView.attachments.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Attachments</p>
                  <ul className="space-y-1">
                    {feedbackToView.attachments.map((att) => (
                      <li key={att.attachment_id} className="flex items-center justify-between gap-2 rounded border bg-muted/30 px-3 py-2">
                        <span className="text-sm truncate flex-1" title={att.original_filename}>
                          {att.original_filename}
                        </span>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {(att.size_bytes / 1024).toFixed(1)} KB
                        </span>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          onClick={() => handleDownloadAttachment(feedbackToView.feedback_id, att.attachment_id)}
                          disabled={downloadingId === `${feedbackToView.feedback_id}-${att.attachment_id}`}
                        >
                          {downloadingId === `${feedbackToView.feedback_id}-${att.attachment_id}` ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Download className="h-4 w-4" />
                          )}
                        </Button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
